import { WEEK_MS } from "./freshness.js";

// The calendar week, from the athlete's `weekStartDay` (Monday by default):
// the weekly bars, the streak and the 4-week reference read it. The actual of
// the weekly targets and of the coverage is the last 7 days instead
// (`rollingWindowStart`, Antenore 26/10, #463).
//
// ⛔ Neither window is the per-muscle ACUTE/CHRONIC state: that one has a
// window of its own and never resets. 📖 next/measurement-contract.md §8bis.

// 0 = Sunday .. 6 = Saturday, matching JS Date.getDay(). Default Monday: the
// user is in the EU and the app already numbers weeks ISO (Monday-anchored).
export const DEFAULT_WEEK_START_DAY = 1;

/**
 * Epoch-ms of the most recent `weekStartDay` at 00:00 LOCAL time, at or before
 * `nowMs` — i.e. the start of the calendar week that contains `nowMs`.
 *
 * Local (not UTC) on purpose: a training week is anchored to the athlete's wall
 * clock. The returned value is still an absolute instant, so comparing a
 * session's absolute `startedAtMs` against it is correct regardless of zone.
 *
 * DST-correct: it walks back whole CALENDAR days via `setDate` (not a fixed
 * `days * 86_400_000`), so a week that contains a clock change still lands on
 * local midnight of the start day, not an hour off.
 */
export const weekWindowStart = (
  nowMs: number,
  weekStartDay: number = DEFAULT_WEEK_START_DAY,
): number => {
  const d = new Date(nowMs);
  // Days elapsed since the most recent start-of-week (0..6).
  const back = (d.getDay() - weekStartDay + 7) % 7;
  d.setHours(0, 0, 0, 0); // local midnight of today
  d.setDate(d.getDate() - back); // walk back to the week's start day
  return d.getTime();
};

/**
 * Start of the calendar week `weeksAgo` before the current one (0 = current).
 * Anchored on the DST-correct current-week start, then stepped back in whole
 * calendar weeks. Used by the 4-week reference so its buckets align to the same
 * weekday boundary the meter scores on.
 */
export const weekStartNAgo = (
  nowMs: number,
  weekStartDay: number,
  weeksAgo: number,
): number => {
  const d = new Date(weekWindowStart(nowMs, weekStartDay));
  d.setDate(d.getDate() - weeksAgo * 7);
  return d.getTime();
};

/**
 * Which 0-based calendar week `startedAtMs` falls in, counting back from the
 * current one: 0 = this week, 1 = last week. -1 when `startedAtMs` is after
 * `nowMs`; NaN for an unparseable date. No upper bound: callers window it.
 */
export const calendarWeeksAgo = (
  startedAtMs: number,
  nowMs: number,
  weekStartDay: number,
): number => {
  if (startedAtMs > nowMs) return -1;
  // Weeks between the two local week starts, rounded: a DST change in between
  // makes the gap an hour off whole weeks, and must not move the session
  // across a boundary (PG-58). NaN stays NaN.
  const gap =
    weekWindowStart(nowMs, weekStartDay) -
    weekWindowStart(startedAtMs, weekStartDay);
  return Math.round(gap / WEEK_MS);
};

/**
 * Epoch-ms of the start of the last `days` days: the same local wall-clock
 * time, `days` calendar days before `nowMs`. The window is [start, nowMs].
 * DST-correct like `weekWindowStart`: it steps back whole calendar days via
 * `setDate`, not a fixed `days * 86_400_000`.
 */
export const rollingWindowStart = (nowMs: number, days = 7): number => {
  const d = new Date(nowMs);
  d.setDate(d.getDate() - days);
  return d.getTime();
};
